import React from 'react'
import { BarChart } from '@mui/x-charts/BarChart'
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine'
import { Paper, Box, Typography, useTheme, useMediaQuery } from '@mui/material'
import { DiscountedCashFlowResponseData } from '../Config/types'
import { tableColors, indicatorColors } from '../Config/colors'
import { roundingConfig } from '../Config/displaySettings'

// Convert RGB array to CSS color string
const rgbToCss = (rgb: number[]): string => `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`

interface ValuationBarChartProps {
  responseData: DiscountedCashFlowResponseData
  maximalGrowthRate?: number
}

export const ValuationBarChart: React.FC<ValuationBarChartProps> = ({ responseData, maximalGrowthRate }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  if (!responseData || !responseData.intrinsic_values || responseData.intrinsic_values.length === 0) {
    return null
  }

  // Prepare data for grouped bar chart
  const companies = responseData.intrinsic_values.map(iv => 
    iv.company_information.symbol
  )

  // Calculate min/max values for both axes
  const marketCapRatios = responseData.intrinsic_values.map(iv => iv.market_cap_to_intrinsic_value_ratio)
  const growthRates = responseData.intrinsic_values.map(iv => iv.discounted_cash_flow.average_annual_growth_rate * 100)
  
  const maxMarketCapRatio = Math.max(...marketCapRatios)
  const minGrowthRate = Math.min(...growthRates)
  const maxGrowthRate = Math.max(...growthRates)

  // Calculate axis ranges to align zero on both axes
  // For market cap ratio (always positive, starts at 0)
  const marketCapAxisMin = 0
  // Include undervalued threshold line at 1 in the range calculation
  const marketCapDataMax = Math.max(maxMarketCapRatio, 1)
  const marketCapAxisMax = Math.ceil(marketCapDataMax * 1.2 * 10) / 10

  // For growth rate (can be negative)
  // We need to ensure both axes align at zero
  // Include maximal growth rate line in the range calculation if provided
  const maxGrowthRateValue = maximalGrowthRate !== undefined ? maximalGrowthRate * 100 : maxGrowthRate
  const growthDataMax = Math.max(maxGrowthRate, maxGrowthRateValue)
  const growthPadding = Math.max(Math.abs(minGrowthRate), Math.abs(growthDataMax)) * 0.2
  const growthAxisMin = minGrowthRate < 0 ? Math.floor(minGrowthRate - growthPadding) : 0
  const growthAxisMax = Math.ceil(growthDataMax + growthPadding)

  // If growth has negative values, adjust market cap axis to align zeros
  let adjustedMarketCapMin = marketCapAxisMin
  let adjustedMarketCapMax = marketCapAxisMax
  
  if (growthAxisMin < 0) {
    // Calculate the proportion of the axis that is below zero for growth
    const growthRange = growthAxisMax - growthAxisMin
    const growthNegativeRatio = Math.abs(growthAxisMin) / growthRange
    
    // Extend market cap axis below zero by the same proportion
    const marketCapPositiveRange = marketCapAxisMax
    adjustedMarketCapMin = -(marketCapPositiveRange * growthNegativeRatio) / (1 - growthNegativeRatio)
  }

  // Calculate dynamic chart height based on viewport and number of companies
  // On mobile, use viewport height to ensure chart fits on one screen
  // Account for navbar (64px), paper padding, title, legend, and description (~180px overhead)
  const getChartHeight = () => {
    if (typeof window === 'undefined') return 400
    const viewportHeight = window.innerHeight
    const overhead = 180 // navbar + padding + title + legend + description
    const availableHeight = viewportHeight - overhead
    
    if (isMobile) {
      // Use 70% of available viewport height, with min/max bounds
      return Math.min(Math.max(availableHeight * 0.7, 250), 500)
    }
    // Desktop: use calculated height based on companies, but cap at reasonable max
    const heightPerCompany = 80
    return Math.min(Math.max(400, companies.length * heightPerCompany + 150), 600)
  }
  const chartHeight = getChartHeight()

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 0, 
        backgroundColor: 'white',
        borderRadius: 2
      }}
    >
      <Typography 
        variant="h6" 
        component="h2" 
        gutterBottom
        sx={{ 
          fontWeight: 600,
          color: theme.palette.primary.main,
          mb: 2,
          pt: 1.5,
          px: 1.5
        }}
      >
        Valuation Overview
      </Typography>
      
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <BarChart
          dataset={responseData.intrinsic_values.map((iv, index) => ({
            company: iv.company_information.symbol,
            companyName: iv.company_information.company_name,
            marketCapRatio: iv.market_cap_to_intrinsic_value_ratio,
            growthRate: iv.discounted_cash_flow.average_annual_growth_rate * 100
          }))}
          xAxis={[
            { 
              scaleType: 'band', 
              dataKey: 'companyName',
              label: 'Company',
              labelStyle: {
                fontSize: isMobile ? 12 : 14,
                fontWeight: 500
              },
              tickLabelStyle: {
                fontSize: isMobile ? 10 : 12,
                angle: companies.length > 3 ? (isMobile ? -45 : -30) : 0,
                textAnchor: companies.length > 3 ? 'end' : 'middle'
              }
            }
          ]}
          yAxis={[
            {
              id: 'marketCapAxis',
              min: adjustedMarketCapMin,
              max: adjustedMarketCapMax,
              label: 'Market Cap / Intrinsic Value',
              labelStyle: {
                fontSize: isMobile ? 11 : 13,
                fontWeight: 500
              },
              tickLabelStyle: {
                fontSize: isMobile ? 10 : 12
              }
            },
            {
              id: 'growthRateAxis',
              min: growthAxisMin,
              max: growthAxisMax,
              position: 'right',
              label: 'Avg. Annual Growth Rate (%)',
              labelStyle: {
                fontSize: isMobile ? 11 : 13,
                fontWeight: 500
              },
              tickLabelStyle: {
                fontSize: isMobile ? 10 : 12
              }
            }
          ]}
          series={[
            {
              id: 'marketCapRatio',
              dataKey: 'marketCapRatio',
              label: 'Market Cap / Intrinsic Value',
              color: theme.palette.primary.main,
              valueFormatter: (value) => value?.toFixed(roundingConfig.bar_chart_labels) || '0',
              yAxisId: 'marketCapAxis',
              barLabel: (item) => item.value != null ? item.value.toFixed(roundingConfig.bar_chart_labels) : '',
            },
            {
              id: 'growthRate',
              dataKey: 'growthRate',
              label: 'Avg. Annual Growth Rate (%)',
              color: theme.palette.secondary.main,
              valueFormatter: (value) => `${value?.toFixed(roundingConfig.bar_chart_labels)}%` || '0%',
              yAxisId: 'growthRateAxis',
              barLabel: (item) => item.value != null ? `${item.value.toFixed(roundingConfig.bar_chart_labels)}%` : '',
            }
          ]}
          height={chartHeight}
          margin={{ 
            top: isMobile ? 40 : 50, 
            right: isMobile ? 35 : 45, 
            bottom: companies.length > 3 ? (isMobile ? 40 : 55) : (isMobile ? 35 : 50), 
            left: isMobile ? 30 : 35 
          }}
          slotProps={{
            legend: {
              position: { vertical: 'top', horizontal: 'center' as const }
            }
          }}
          sx={{
            '& .MuiChartsAxis-line': {
              stroke: theme.palette.divider
            },
            '& .MuiChartsAxis-tick': {
              stroke: theme.palette.divider
            },
            '& .MuiChartsAxis-tickLabel': {
              fill: theme.palette.text.secondary
            },
            '& .MuiChartsAxis-label': {
              fill: theme.palette.text.primary
            },
            '& .MuiBarLabel-root': {
              fontSize: isMobile ? 10 : 12,
              fontWeight: 500,
              fill: theme.palette.text.primary
            }
          }}
        >
          <ChartsReferenceLine
            y={0}
            lineStyle={{
              stroke: theme.palette.text.secondary,
              strokeWidth: 1.5,
              strokeDasharray: '4 2'
            }}
          />
          {/* Green dashed line at Market Cap / Intrinsic Value = 1 (undervalued threshold) */}
          <ChartsReferenceLine
            y={1}
            axisId="marketCapAxis"
            label="Fair Value"
            lineStyle={{
              stroke: rgbToCss(indicatorColors.fontColors.green),
              strokeWidth: 2,
              strokeDasharray: '6 4'
            }}
            labelStyle={{
              fontSize: isMobile ? 10 : 12,
              fill: rgbToCss(indicatorColors.fontColors.green)
            }}
          />
          {/* Red dashed line at maximal growth rate (if specified) */}
          {maximalGrowthRate !== undefined && (
            <ChartsReferenceLine
              y={maximalGrowthRate * 100}
              axisId="growthRateAxis"
              label="Max Growth Rate"
              lineStyle={{
                stroke: rgbToCss(indicatorColors.fontColors.red),
                strokeWidth: 2,
                strokeDasharray: '6 4'
              }}
              labelStyle={{
                fontSize: isMobile ? 10 : 12,
                fill: rgbToCss(indicatorColors.fontColors.red)
              }}
            />
          )}
        </BarChart>
      </Box>
      
      {/* Custom legend for reference lines */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        gap: 2, 
        justifyContent: 'center', 
        mt: 0,
        mb: 0.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ 
            width: 24, 
            height: 0, 
            borderTop: `2px dashed ${rgbToCss(indicatorColors.fontColors.green)}`
          }} />
          <Typography variant="caption" color="text.secondary">
            Undervalued Threshold (Ratio = 1)
          </Typography>
        </Box>
        {maximalGrowthRate !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 24, 
              height: 0, 
              borderTop: `2px dashed ${rgbToCss(indicatorColors.fontColors.red)}`
            }} />
            <Typography variant="caption" color="text.secondary">
              Max Growth Rate ({(maximalGrowthRate * 100).toFixed(0)}%)
            </Typography>
          </Box>
        )}
      </Box>
      
      <Box sx={{ mt: 1, p: 1.5, backgroundColor: tableColors.describingCellBackground, borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          <strong>Market Cap / Intrinsic Value:</strong> A ratio below 1.0 suggests the stock is undervalued.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          <strong>Avg. Annual Growth Rate:</strong> Expected annual growth rate over the valuation period.
        </Typography>
      </Box>
    </Paper>
  )
}

export default ValuationBarChart
